import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Description from '@mui/icons-material/Description';
import HourglassTop from '@mui/icons-material/HourglassTop';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { monoSx } from '../dashboard/styles';

export interface CreatedJob {
  jobId: string;
  /** Seconds until the redirect to the dashboard. */
  countdown: number;
}

interface GenerateBarProps {
  /** Summary of what will be generated; null when no source is chosen yet. */
  hint: string | null;
  submitting: boolean;
  created: CreatedJob | null;
  onSubmit: () => void;
}

/** Sticky footer with the scope summary and the primary action. */
const GenerateBar: React.FC<GenerateBarProps> = ({ hint, submitting, created, onSubmit }) => {
  const blocked = !hint || submitting || !!created;
  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 16,
        zIndex: 4,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2,
        mt: 1,
        py: 1.5,
        pr: 1.5,
        pl: 2.5,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 4,
        boxShadow: '0 6px 24px rgba(0,21,41,.10)',
      }}
    >
      <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, color: created ? 'brand.onContainer' : 'text.secondary', minWidth: 0 }}>
        {created ? (
          <>
            <CheckCircle sx={{ fontSize: 20, color: 'primary.dark' }} />
            <span>
              Job <Box component="strong" sx={monoSx}>{created.jobId}</Box> created · redirecting in {created.countdown}s ·{' '}
              <Link component={RouterLink} to="/dashboard" sx={{ color: 'inherit', fontWeight: 600 }}>
                Go to dashboard
              </Link>
            </span>
          </>
        ) : hint ? (
          <>
            <InfoOutlined sx={{ fontSize: 18 }} />
            {hint}
          </>
        ) : (
          <>
            <ArrowUpward sx={{ fontSize: 18 }} />
            Choose a Swagger source to continue
          </>
        )}
      </Box>
      <Button
        variant="contained"
        disableElevation
        onClick={onSubmit}
        disabled={blocked}
        startIcon={submitting ? <HourglassTop /> : <Description />}
        sx={{
          ml: 'auto',
          height: 48,
          pl: 2.5,
          pr: 3,
          borderRadius: 6,
          fontSize: 15,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 1px 2px rgba(0,21,41,.15)',
          '&.Mui-disabled': { bgcolor: 'action.hover', color: 'text.secondary' },
        }}
      >
        {submitting ? 'Generating…' : 'Generate mapping document'}
      </Button>
    </Box>
  );
};

export default GenerateBar;
